import {
  MDBBtn,
  MDBCard,
  MDBCardBody,
  MDBCardTitle,
  MDBIcon,
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalHeader,
  MDBModalBody,
  MDBModalTitle,
  MDBBadge,
  MDBInput,
  MDBDropdown,
  MDBDropdownToggle,
  MDBDropdownMenu,
  MDBDropdownItem,
} from "mdb-react-ui-kit";
import { useState, useEffect, useContext, useMemo, useCallback, memo } from "react"
import { useTranslation } from "react-i18next"
import { Link, useNavigate } from "react-router-dom";
import { generateClient } from "aws-amplify/api"
import { fetchAuthSession } from "aws-amplify/auth"
import { UserContext } from "../../../App"
import { isoToGermanDate } from "../../../utils/dateTools"
import { deleteTicket } from "../../../graphql/mutations"
import ShowTicket from "../../../components-user/tickets/ShowTicket"
import StripeRedirectingOverlay from "../../../components-user/StripeRedirectingOverlay"
import { logError } from "../../../utils/logging"
import { captureError } from '../../../utils/sentryHelpers'
import { useCurrentEvent } from "../../../utils/useCurrentEvent"
import { getUserBookingsQuery } from "./queries/getUserBookingsQuery"

const client = generateClient()

// Lambda-backed mutations (@function @auth(allow: private)) need the Cognito
// ID token sent explicitly — Amplify's client.graphql defaults to the access
// token, which lacks custom: claims that the Lambda reads.
const GRAPHQL_ENDPOINT = "https://y3vayhw2i5bqrm6fpwqz7fzfwe.appsync-api.eu-central-1.amazonaws.com/graphql"

const CREATE_STRIPE_CHECKOUT_SESSION = /* GraphQL */ `
  mutation CreateStripeCheckoutSession(
    $orderId: String!
    $successUrl: String!
    $cancelUrl: String!
  ) {
    createStripeCheckoutSession(
      orderId: $orderId
      successUrl: $successUrl
      cancelUrl: $cancelUrl
    )
  }
`;

/** Get localized text from a name array [{language, text}] */
const getLocalizedText = (nameArray, language) =>
  nameArray?.find((n) => n.language === language)?.text || nameArray?.[0]?.text || "";

/** Renders company logo (if available) + name */
const CompanyDisplay = memo(({ logoFileName, name, size = 26 }) => {
  if (!name) return null;
  return (
    <span className="d-inline-flex align-items-center gap-1">
      {logoFileName && (
        <img
          src={`https://iec-cp-public.s3.eu-central-1.amazonaws.com/companyImages/${logoFileName}`}
          alt={name}
          className="rounded-circle bg-white p-1"
          style={{ width: `${size}px`, height: `${size}px`, objectFit: "contain" }}
        />
      )}
      <span>{name}</span>
    </span>
  );
});

/** Format event days as compact date range */
const formatEventDays = (days) => {
  if (!days?.length) return "";
  if (days.length === 1) return isoToGermanDate(days[0]);
  return `${isoToGermanDate(days[0])} – ${isoToGermanDate(days[days.length - 1])}`;
};

/** Check if an event is in the past (all event days before today) */
const isEventPast = (eventDays) => {
  if (!eventDays?.length) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return eventDays.every((day) => new Date(day) < today);
};

/** Extract event from an order via fallback chain:
 *  1. order.event (direct)
 *  2. lineItem.bookedTicket.event
 *  3. lineItem.bookedBooth.event
 *  4. lineItem.bookedBoothEquipment.bookedBooth.event
 *
 *  Orders without any of these end up in a standalone group (see
 *  buildEventGroups step 3) — never guessed via other company orders,
 *  which would put 2026 purchases under unrelated 2027 tabs.
 */
const getEventFromOrder = (order) => {
  if (order.event?.id) return order.event;
  for (const li of order.lineItems?.items || []) {
    if (li.bookedTicket?.event?.id) return li.bookedTicket.event;
    if (li.bookedBooth?.event?.id) return li.bookedBooth.event;
    if (li.bookedBoothEquipment?.bookedBooth?.event?.id) return li.bookedBoothEquipment.bookedBooth.event;
  }
  return null;
};

// ─── Status Badge ───────────────────────────────────────────

const STATUS_CONFIG = {
  paid: { color: "success", icon: "check-circle", labelKey: "Paid" },
  unpaid: { color: "danger", icon: "times-circle", labelKey: "Unpaid" },
  canceled: { color: "danger", icon: "ban", labelKey: "Canceled" },
  refunded: { color: "warning", icon: "undo", labelKey: "Refunded" },
  partially_paid: { color: "warning", icon: "clock", labelKey: "Partially paid" },
  pending: { color: "info", icon: "hourglass-half", labelKey: "Pending" },
  review: { color: "warning", icon: "hourglass-start", labelKey: "In review" },
};

const StatusBadge = memo(({ status, t }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.unpaid;
  return (
    <MDBBadge color={config.color} pill className="px-3 py-2">
      <MDBIcon fas icon={config.icon} className="me-1" />
      {t(config.labelKey)}
    </MDBBadge>
  );
});

/** Derive a normalized payment status string from order/ticket data */
const getPaymentStatus = (item) => {
  if (item.status === "canceled") return "canceled";
  return item.paymentStatus ?? "paid"; // null = free ticket → treat as paid
};

// ─── Data Grouping Logic ────────────────────────────────────

/**
 * Hide cancelled orders that never produced a financial document
 * (Rechnung/Gutschrift). Such orders are essentially abandoned drafts
 * — showing them only confuses exhibitors.
 */
const hasActiveFinancialDoc = (order) =>
  (order.financial?.documents ?? []).some((d) => d.status !== "deleted");

const isVisibleOrder = (order) =>
  order.status !== "canceled" || hasActiveFinancialDoc(order);

/**
 * Groups tickets and orders by event, producing a structure like:
 * [{ eventId, eventName, eventDays, tickets: [], orders: [], latestDate, isPast }]
 *
 * Orders without a matched ticket go into a special "standalone" group.
 */
function buildEventGroups(tickets, orders, language, t) {
  const ticketMap = new Map(tickets.map((t) => [t.id, t]));
  const groups = new Map(); // eventId -> group
  const matchedTicketIds = new Set();
  const matchedOrderIds = new Set();

  const visibleOrders = orders.filter(isVisibleOrder);

  // 1) Match orders to tickets → derive eventId from ticket
  visibleOrders.forEach((order) => {
    order.lineItems?.items?.forEach((li) => {
      const ticketId = li.bookedTicket?.id;
      if (!ticketId) return;
      const ticket = ticketMap.get(ticketId);
      if (!ticket) return;

      const eventId = ticket.event?.id || "unknown";
      if (!groups.has(eventId)) {
        groups.set(eventId, {
          eventId,
          eventName: getLocalizedText(ticket.event?.name, language),
          eventDays: ticket.eventDays || [],
          tickets: [],
          orders: [],
          latestDate: ticket.createdAt,
          isPast: isEventPast(ticket.eventDays),
        });
      }

      const group = groups.get(eventId);
      // Avoid duplicate tickets in the same group
      if (!matchedTicketIds.has(ticket.id)) {
        group.tickets.push(ticket);
        matchedTicketIds.add(ticket.id);
      }
      // Avoid duplicate orders
      if (!matchedOrderIds.has(order.id)) {
        group.orders.push(order);
        matchedOrderIds.add(order.id);
        // Track latest date
        if (new Date(order.createdAt) > new Date(group.latestDate)) {
          group.latestDate = order.createdAt;
        }
      }
    });
  });

  // 2) Remaining unmatched tickets → own event groups
  tickets.forEach((ticket) => {
    if (matchedTicketIds.has(ticket.id)) return;
    const eventId = ticket.event?.id || `ticket-${ticket.id}`;
    if (!groups.has(eventId)) {
      groups.set(eventId, {
        eventId,
        eventName: getLocalizedText(ticket.event?.name, language),
        eventDays: ticket.eventDays || [],
        tickets: [],
        orders: [],
        latestDate: ticket.createdAt,
        isPast: isEventPast(ticket.eventDays),
      });
    }
    groups.get(eventId).tickets.push(ticket);
    matchedTicketIds.add(ticket.id);
  });

  // 3) Remaining unmatched orders → group by event if available
  visibleOrders.forEach((order) => {
    if (matchedOrderIds.has(order.id)) return;
    const event = getEventFromOrder(order);
    const eventId = event?.id || `order-${order.id}`;
    const eventName = event?.name
      ? getLocalizedText(event.name, language)
      : t("Order");

    if (!groups.has(eventId)) {
      groups.set(eventId, {
        eventId,
        eventName,
        eventDays: [],
        tickets: [],
        orders: [],
        latestDate: order.createdAt,
        isPast: false,
        isStandaloneOrder: !event?.id,
        companyLogo: !event?.id ? order.company?.image?.main?.fileName : undefined,
      });
    }

    const group = groups.get(eventId);
    group.orders.push(order);
    if (new Date(order.createdAt) > new Date(group.latestDate)) {
      group.latestDate = order.createdAt;
    }
  });

  // Sort items within each group by date (newest first)
  const result = Array.from(groups.values());
  result.forEach(group => {
    group.orders.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    group.tickets.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  });

  // Sort groups strictly by the most recent activity (latest order/ticket first),
  // regardless of whether the event itself is in the past.
  return result.sort((a, b) => new Date(b.latestDate) - new Date(a.latestDate));
}

// ─── Filter Toolbar ─────────────────────────────────────────

const FILTER_OPTIONS = [
  { value: "all", labelKey: "All" },
  { value: "paid", labelKey: "Paid" },
  { value: "unpaid", labelKey: "Unpaid" },
  { value: "partially_paid", labelKey: "Partially paid" },
  { value: "canceled", labelKey: "Canceled" },
  { value: "pending", labelKey: "Pending" },
  { value: "review", labelKey: "In review" },
];

const FilterToolbar = memo(({ searchQuery, onSearchChange, statusFilter, onStatusChange, resultCount, filteredGroups, toggleAll, allExpanded, t }) => (
  <div className="d-flex flex-column flex-md-row gap-3 mb-4 align-items-md-center">
    {/* Search */}
    <div className="flex-grow-1">
      <MDBInput
        type="text"
        label={t("Search events, names...")}
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
        className=""
      >
        <MDBIcon fas icon="search" className="trailing" />
      </MDBInput>
    </div>

    {/* Status Filter & Count row on mobile */}
    <div className="d-flex align-items-center gap-3">
      {filteredGroups.length > 0 && (
        <MDBBtn
          size="sm"
          color="light"
          onClick={toggleAll}
          className="d-flex justify-content-center align-items-center gap-2"
          style={{ width: window.innerWidth >= 768 ? "148px" : "fit-content" }}
        >
          <MDBIcon fas icon={allExpanded ? "angles-up" : "angles-down"} />
          <span className="d-none d-md-inline">
            {allExpanded ? t("Collapse all") : t("Expand all")}
          </span>
        </MDBBtn>
      )}
      <MDBDropdown>
        <MDBDropdownToggle color="light" size="sm" className="px-3 text-capitalize">
          <MDBIcon fas icon="filter" className="me-2" />
          {t("Status")}: {t(FILTER_OPTIONS.find((o) => o.value === statusFilter)?.labelKey || "All")}
        </MDBDropdownToggle>
        <MDBDropdownMenu>
          {FILTER_OPTIONS.map((opt) => (
            <MDBDropdownItem
              key={opt.value}
              link
              onClick={() => onStatusChange(opt.value)}
              className={statusFilter === opt.value ? "active" : ""}
            >
              {t(opt.labelKey)}
            </MDBDropdownItem>
          ))}
        </MDBDropdownMenu>
      </MDBDropdown>

      {/* Result count */}
      <span className="small text-nowrap">
        {resultCount} {t(resultCount === 1 ? "Item" : "Items")}
      </span>
    </div>
  </div>
));

// ─── Ticket Item ────────────────────────────────────────────

const TicketItem = memo(({ ticket, paymentProvider, onView, onPay, t }) => {
  const status = getPaymentStatus(ticket);
  // Decide "Pay ticket" by combining two signals:
  //   1. Per-booking truth (OrderFinancial.paymentProvider) — immutable, wins on collision
  //      (e.g. invoice-pending order on an event the admin later flipped to Stripe).
  //   2. Event config fallback (instantPaymentOnly) — for tickets without a linked order
  //      (legacy / free) or when financial data hasn't been created yet.
  const paymentPending = ticket.paymentStatus === "unpaid" || ticket.paymentStatus === "pending";
  const effectiveProvider = paymentProvider
    ?? (ticket.event?.instantPaymentOnly === true ? "stripe" : "invoice");
  const needsPayment = effectiveProvider === "stripe" && paymentPending;
  const invoicePending = effectiveProvider === "invoice" && paymentPending;

  return (
    <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-center gap-2 py-2 px-3 rounded-3" style={{ backgroundColor: "var(--mdb-tertiary-bg)" }}>
      <div className="d-flex align-items-center gap-3">
        <MDBIcon fas icon="ticket" className="text-primary flex-shrink-0" size="lg" />
        <div className="min-w-0 flex-grow-1" style={{ minWidth: 0 }}>
          <div className="fw-semibold small text-truncate">
            {ticket.personalAddress?.firstName} {ticket.personalAddress?.lastName}
            {ticket.type && ticket.type !== "default" && (
              <MDBBadge color="info" pill className="ms-2 text-capitalize small">
                {ticket.type}
              </MDBBadge>
            )}
          </div>
          <div className="text-muted small">
            {formatEventDays(ticket.eventDays)}
          </div>
          <div className="text-muted" style={{ fontSize: "0.75rem", opacity: 0.75 }}>
            {t("Booking date")}: {isoToGermanDate(ticket.createdAt)}
          </div>
        </div>
      </div>
      <div className="d-flex align-items-center flex-column flex-md-row gap-2 mt-2">
        <StatusBadge status={status} t={t} />
        {invoicePending && (
          <MDBBadge color="warning" pill className="px-2 py-1 small text-nowrap">
            <MDBIcon fas icon="file-invoice" className="me-1" />
            {t("Invoice outstanding")}
          </MDBBadge>
        )}
        {needsPayment ? (
          <MDBBtn color="success" size="sm" className="px-3 text-nowrap" onClick={() => onPay(ticket)}>
            <MDBIcon fas icon="credit-card" className="me-1" />
            {t("Pay ticket")}
          </MDBBtn>
        ) : (
          <MDBBtn color="link" size="sm" className="p-1 text-nowrap" onClick={() => onView(ticket)}>
            {t("Show ticket")}
          </MDBBtn>
        )}
      </div>
    </div>
  );
});

// ─── Order Item ─────────────────────────────────────────────

/** First product image among the order's line items (for the thumbnail). */
const getOrderThumbnail = (order) => {
  for (const li of order.lineItems?.items || []) {
    const fileName = li.product?.image?.main?.fileName;
    if (!fileName) continue;
    const folder = li.product?.type === "ticket" || li.product?.type === "event_booth"
      ? "EventImages"
      : "ProductImages";
    return `https://iec-cp-public.s3.eu-central-1.amazonaws.com/${folder}/${fileName}`;
  }
  return null;
};

const OrderItem = memo(({ order, onView, t, language }) => {
  const thumb = getOrderThumbnail(order);
  return (
    <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-center gap-2 py-2 px-3 rounded-3" style={{ backgroundColor: "var(--mdb-tertiary-bg)" }}>
      <div className="d-flex align-items-center gap-3 w-100">
        {thumb && (
          <img
            src={thumb}
            alt=""
            className="rounded bg-white p-1 flex-shrink-0"
            style={{ width: 48, height: 48, objectFit: "contain" }}
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        )}
        <div className="flex-grow-1">
          {order.lineItems?.items?.map((li, idx) => (
            <div
              key={li.id || idx}
              className={`small ${li.isCanceled ? "text-decoration-line-through text-danger" : ""}`}
            >
              {getLocalizedText(li.product?.name, language)}
              {li.qty > 1 && <span className="text-muted"> × {li.qty}</span>}
              {li.isCanceled && (
                <span className="text-danger ms-1 small">
                  ({t("Cancelled")}
                  {li.cancelationDate && ` ${isoToGermanDate(li.cancelationDate)}`})
                </span>
              )}
            </div>
          ))}
          <div className="text-muted small mt-2">
            {t("Order date")}: {isoToGermanDate(order.createdAt)}
            {order.totalAmount != null && (
              <span className="ms-2 fw-semibold">
                {order.totalAmount.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="d-flex align-items-center flex-column flex-md-row gap-2 mt-2">
        <StatusBadge status={getPaymentStatus(order)} t={t} />
        <MDBBtn color="link" size="sm" className="p-1 text-nowrap" onClick={() => onView(order)}>
          {t("Show Order")}
        </MDBBtn>
      </div>
    </div>
  );
});

// ─── Event Group Card ───────────────────────────────────────

const EventGroupCard = memo(({ group, isExpanded, onToggle, onViewTicket, onPayTicket, onViewOrder, orderByTicketId, t, language }) => {
  const ticketCount = group.tickets.length;
  const orderCount = group.orders.length;
  const hasUnpaid = [...group.tickets, ...group.orders].some(
    (item) => ["unpaid", "partially_paid", "pending"].includes(getPaymentStatus(item))
  );
  const handleToggle = useCallback(() => onToggle(group.eventId), [onToggle, group.eventId]);

  return (
    <MDBCard className={`mb-3 border ${group.isPast ? "border-light" : "border-0 shadow-sm"}`}>
      {/* Clickable Header */}
      <div
        className={`d-flex align-items-start justify-content-between p-3 rounded-top${group.isPast ? " opacity-75" : ""}`}
        onClick={handleToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && handleToggle()}
        aria-expanded={isExpanded}
      >
        <div className="d-flex align-items-start gap-2 min-w-0 flex-grow-1" style={{ minWidth: 0 }}>
          {/* Expand icon */}
          <div className="d-inline-flex align-items-center justify-content-center flex-shrink-0 pt-1" style={{ width: "24px" }}>
            <MDBIcon
              fas
              icon={isExpanded ? "chevron-down" : "chevron-right"}
              className="text-muted"
            />
          </div>

          {/* Event icon or Company Logo */}
          {group.companyLogo ? (
            <CompanyDisplay logoFileName={group.companyLogo} name="" size={36} />
          ) : (
            <div className="flex-shrink-0 pt-1">
              <MDBIcon
                fas
                icon={group.isStandaloneOrder ? "receipt" : "calendar-alt"}
                className="text-primary"
                size="lg"
              />
            </div>
          )}

          {/* Event name & meta */}
          <div className="min-w-0" style={{ minWidth: 0 }}>
            <div className={`fw-bold d-flex${group.isPast ? "text-muted" : ""}`} style={{ wordBreak: "break-word" }}>
              {group.isStandaloneOrder && group.orders[0]?.company?.name
                ? <>{t("Order")} {t("for")}: <CompanyDisplay logoFileName={group.companyLogo} name={group.orders[0].company.name} size={24} /></>
                : group.eventName
              }
              {group.isPast && (
                <MDBBadge color="light" className="ms-2 text-muted small">
                  {t("Past")}
                </MDBBadge>
              )}
            </div>
            <div className="text-muted small" style={{ lineHeight: "1.6" }}>
              {group.eventDays.length > 0 && <span className="me-3">{formatEventDays(group.eventDays)}</span>}
              {ticketCount > 0 && (
                <span className="me-3 text-nowrap">
                  <MDBIcon fas icon="ticket" size="sm" className="me-1" />
                  {ticketCount} {t(ticketCount === 1 ? "Ticket" : "Tickets")}
                </span>
              )}
              {orderCount > 0 && (
                <span className="text-nowrap">
                  <MDBIcon fas icon="file-invoice" size="sm" className="me-1" />
                  {orderCount} {t(orderCount === 1 ? "Order" : "Orders")}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Right side: status summary */}
        <div className="d-flex flex-column align-items-end gap-1 flex-shrink-0 ms-2">
          {hasUnpaid && (
            <MDBBadge color="danger" pill className="px-2 py-1 small text-nowrap">
              <MDBIcon fas icon="exclamation-triangle" className="me-1" />
              <span className="d-none d-sm-inline">{t("Payment pending")}</span>
              <span className="d-inline d-sm-none">!</span>
            </MDBBadge>
          )}
          <div className="text-muted small d-none d-md-block text-nowrap">{isoToGermanDate(group.latestDate)}</div>
        </div>
      </div>

      {/* Expandable Content */}
      <div className={`collapse-smooth${isExpanded ? " open" : ""}`}>
        <MDBCardBody className="pt-0 pb-2 px-2 px-md-3">
          <hr className="mt-0 mb-3" />

          {/* Orders — rendered before tickets */}
          {group.orders.length > 0 && (
            <div>
              <div className="d-flex align-items-center text-uppercase text-muted fw-bold small mb-2">
                <MDBIcon className="me-2" fas icon="file-invoice" />
                <span>{t("Orders")}{!group.isStandaloneOrder && group.orders[0]?.company?.name && ` ${t("for")}:`}</span>
                {!group.isStandaloneOrder && group.orders[0]?.company?.name && (
                  <CompanyDisplay logoFileName={group.orders[0].company.image?.main?.fileName} name={group.orders[0].company.name} />
                )}
              </div>
              {group.orders.map((order, idx) => (
                <div key={order.id}>
                  {idx > 0 && <hr className="my-2 opacity-25" />}
                  <OrderItem order={order} onView={onViewOrder} t={t} language={language} />
                </div>
              ))}
            </div>
          )}

          {/* Tickets */}
          {group.tickets.length > 0 && (
            <div className={group.orders.length > 0 ? "mt-4" : ""}>
              <div className="text-uppercase text-muted fw-bold small mb-2">
                <MDBIcon fas icon="ticket" className="me-1" />
                {t("Tickets")}
              </div>
              {group.tickets.map((ticket) => (
                <TicketItem
                  key={ticket.id}
                  ticket={ticket}
                  paymentProvider={orderByTicketId?.get(ticket.id)?.paymentProvider}
                  onView={onViewTicket}
                  onPay={onPayTicket}
                  t={t}
                  language={language}
                />
              ))}
            </div>
          )}
        </MDBCardBody>
      </div>
    </MDBCard>
  );
});

// ─── Empty State ────────────────────────────────────────────

const EmptyState = memo(({ hasFilters, t }) => (
  <div className="text-center py-5">
    <MDBIcon fas icon={hasFilters ? "search" : "calendar-plus"} size="3x" className="text-muted mb-3" />
    <h5 className="text-muted">
      {hasFilters ? t("No results match your filters") : t("No bookings yet")}
    </h5>
    <p className="text-muted small">
      {hasFilters
        ? t("Try adjusting your search or filter criteria")
        : t("Your tickets and orders will appear here once you make a booking")}
    </p>
  </div>
));

// ─── Main Component ─────────────────────────────────────────

export default function AllBookings({ isAdmin }) {
  const { user } = useContext(UserContext);
  const { t, i18n } = useTranslation();
  const { currentEvent } = useCurrentEvent();
  const navigate = useNavigate();

  const [tickets, setTickets] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Expanded groups (track by eventId)
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  // Modals
  const [viewTicketModal, setViewTicketModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [viewOrderModal, setViewOrderModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Stripe re-pay flow for existing orders
  const [payError, setPayError] = useState(null);
  const [isProcessingCheckout, setIsProcessingCheckout] = useState(false);

  // ── Data Fetching ───────────────────────────────────────

  const fetchBookings = useCallback(async (userId) => {
    setLoading(true);
    try {
      const { data } = await client.graphql({
        query: getUserBookingsQuery,
        variables: { id: userId },
      });

      setTickets(data.getUser.tickets.items);
      setOrders(data.getUser.orders.items);
    } catch (error) {
      console.error(error);
      captureError(error, {
        feature: "ticket-management",
        operation: "fetch-orders-and-tickets",
        context: { userId },
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user.id) fetchBookings(user.id);
  }, [user.id, fetchBookings]);

  // ── Build & Filter Event Groups ─────────────────────────

  const eventGroups = useMemo(
    () => buildEventGroups(tickets, orders, i18n.language, t),
    [tickets, orders, i18n.language]
  );

  // Map ticketId → { orderId, paymentProvider } for the linked order.
  // Source of truth for deciding which payment UI to show per ticket
  // (independent of the event's current instantPaymentOnly toggle) and
  // for re-paying the existing order via Stripe without creating a duplicate.
  const orderByTicketId = useMemo(() => {
    const map = new Map();
    orders.forEach((order) => {
      order.lineItems?.items?.forEach((li) => {
        const ticketId = li.bookedTicket?.id;
        if (ticketId) {
          map.set(ticketId, {
            orderId: order.id,
            paymentProvider: order.financial?.paymentProvider,
          });
        }
      });
    });
    return map;
  }, [orders]);

  const filteredGroups = useMemo(() => {
    let result = eventGroups;

    // Text search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((group) => {
        const nameMatch = group.eventName.toLowerCase().includes(q);
        const ticketNameMatch = group.tickets.some(
          (t) =>
            `${t.personalAddress?.firstName} ${t.personalAddress?.lastName}`.toLowerCase().includes(q)
        );
        const orderProductMatch = group.orders.some((o) =>
          o.lineItems?.items?.some((li) =>
            getLocalizedText(li.product?.name, i18n.language).toLowerCase().includes(q)
          )
        );
        return nameMatch || ticketNameMatch || orderProductMatch;
      });
    }

    // Status filter — narrow each group to only matching tickets/orders,
    // then drop groups left empty. Without the inner filter the user would
    // see the whole event (incl. unrelated paid items) just because one
    // item matched the selected status.
    if (statusFilter !== "all") {
      result = result
        .map((group) => ({
          ...group,
          tickets: group.tickets.filter((t) => getPaymentStatus(t) === statusFilter),
          orders: group.orders.filter((o) => getPaymentStatus(o) === statusFilter),
        }))
        .filter((group) => group.tickets.length > 0 || group.orders.length > 0);
    }

    return result;
  }, [eventGroups, searchQuery, statusFilter, i18n.language]);

  // Auto-expand groups with unpaid items or if only a few results
  useEffect(() => {
    if (filteredGroups.length <= 3) {
      setExpandedGroups(new Set(filteredGroups.map((g) => g.eventId)));
    }
  }, [filteredGroups.length]);

  // ── Handlers ────────────────────────────────────────────

  const toggleGroup = useCallback((eventId) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      next.has(eventId) ? next.delete(eventId) : next.add(eventId);
      return next;
    });
  }, []);

  const handleViewTicket = useCallback((ticket) => {
    setSelectedTicket(ticket);
    setViewTicketModal(true);
  }, []);

  const handleViewOrder = useCallback((order) => {
    setSelectedOrder(order);
    setViewOrderModal(true);
  }, []);

  const handleDeleteTicket = useCallback(
    async (id) => {
      try {
        await client.graphql({
          query: deleteTicket,
          variables: { input: { id } },
        });
        setTickets((prev) => prev.filter((t) => t.id !== id));
        setViewTicketModal(false);
      } catch (error) {
        console.error(error);
        logError({ message: error.message, userId: user.id, additionalData: { ticketId: id } });
        captureError(error, {
          feature: "ticket-management",
          operation: "delete-ticket",
          context: { userId: user.id, ticketId: id },
        });
      }
    },
    [user.id]
  );

  // Re-pay an existing order via Stripe Hosted Checkout (redirect) instead of
  // starting a new booking flow (which would duplicate the ticket).
  const handlePayTicket = useCallback(async (ticket) => {
    setPayError(null);
    const orderId = orderByTicketId.get(ticket.id)?.orderId;
    if (!orderId) {
      captureError(new Error("Pay ticket: no order linked to ticket"), {
        feature: "ticket-management",
        operation: "pay-existing-ticket",
        context: { ticketId: ticket.id, eventId: ticket.event?.id },
      });
      setPayError(t("Unable to start payment process. Please try again or contact support."));
      return;
    }
    setIsProcessingCheckout(true);
    try {
      const { idToken } = (await fetchAuthSession()).tokens ?? {};
      if (!idToken) throw new Error("Not authenticated");
      const successUrl = `${window.location.origin}/book-ticket?ticketId=${ticket.id}&orderId=${orderId}&session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = window.location.href;
      const resp = await fetch(GRAPHQL_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: idToken.toString() },
        body: JSON.stringify({
          query: CREATE_STRIPE_CHECKOUT_SESSION,
          variables: { orderId, successUrl, cancelUrl },
        }),
      });
      const body = await resp.json();
      const gqlError = body.errors?.[0]?.message;
      if (gqlError) throw new Error(gqlError);
      const result = JSON.parse(body.data.createStripeCheckoutSession);
      if (!result.sessionUrl) throw new Error("Stripe did not return a session URL");
      window.location.href = result.sessionUrl;
    } catch (error) {
      captureError(error, {
        feature: "ticket-management",
        operation: "pay-existing-ticket",
        context: { ticketId: ticket.id, orderId },
      });
      setPayError(error.message || t("Unable to start payment process. Please try again or contact support."));
      setIsProcessingCheckout(false);
    }
  }, [orderByTicketId, t]);

  const toggleAll = useCallback(() => {
    setExpandedGroups(prev => {
      const allCurrentlyExpanded = filteredGroups.length > 0 && filteredGroups.every(g => prev.has(g.eventId));
      if (allCurrentlyExpanded) {
        const next = new Set(prev);
        filteredGroups.forEach(g => next.delete(g.eventId));
        return next;
      } else {
        const next = new Set(prev);
        filteredGroups.forEach(g => next.add(g.eventId));
        return next;
      }
    });
  }, [filteredGroups]);

  const hasFilters = searchQuery.trim() !== "" || statusFilter !== "all";
  const allExpanded = filteredGroups.length > 0 && filteredGroups.every(g => expandedGroups.has(g.eventId));
  const hasTicketForCurrentEvent = currentEvent?.id && tickets.some(t => t.event?.id === currentEvent.id);

  // ── Render ──────────────────────────────────────────────

  return (
    <>
      <MDBCard>
        <MDBCardBody>
          {/* Header */}
          <div className="d-flex align-items-center justify-content-between mb-4 gap-3">
            <MDBCardTitle className="fs-4 fs-md-2 mb-0">{t("Bookings & Tickets")}</MDBCardTitle>
            {currentEvent?.id && (!hasTicketForCurrentEvent || isAdmin) && (
              <Link
                className={`ripple ripple-surface btn btn-primary${currentEvent.enableTicketBooking ? "" : " disabled"}`}
                to={currentEvent.enableTicketBooking ? `/book-ticket?eventId=${currentEvent.id}` : "#"}
                state={{ event: currentEvent }}
                aria-disabled={!currentEvent.enableTicketBooking}
                tabIndex={currentEvent.enableTicketBooking ? undefined : -1}
                onClick={currentEvent.enableTicketBooking ? undefined : (e) => e.preventDefault()}
              >
                {t("Book a ticket for")}{" "}
                {currentEvent.name?.find((n) => n.language === i18n.language)?.text ||
                  currentEvent.name?.[0]?.text}
              </Link>
            )}
          </div>

          {/* Filters */}
          <FilterToolbar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
            resultCount={filteredGroups.length}
            filteredGroups={filteredGroups}
            toggleAll={toggleAll}
            allExpanded={allExpanded}
            t={t}
          />

          {/* Loading */}
          {loading && (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">{t("Loading...")}</span>
              </div>
            </div>
          )}

          {payError && (
            <div className="alert alert-danger d-flex align-items-center" role="alert">
              <MDBIcon fas icon="exclamation-triangle" className="me-2" />
              <span>{payError}</span>
            </div>
          )}

          {/* Event Groups */}
          {!loading && filteredGroups.length === 0 && <EmptyState hasFilters={hasFilters} t={t} />}

          {!loading &&
            filteredGroups.map((group) => (
              <EventGroupCard
                key={group.eventId}
                group={group}
                isExpanded={expandedGroups.has(group.eventId)}
                onToggle={toggleGroup}
                onViewTicket={handleViewTicket}
                onPayTicket={handlePayTicket}
                onViewOrder={handleViewOrder}
                orderByTicketId={orderByTicketId}
                t={t}
                language={i18n.language}
              />
            ))}
        </MDBCardBody>
      </MDBCard>

      {/* ── Ticket Modal ───────────────────────────────── */}
      <MDBModal open={viewTicketModal} onClose={() => setViewTicketModal(false)} position="top">
        <MDBModalDialog>
          <MDBModalContent>
            <MDBModalBody>
              <MDBBtn className="btn-close float-end" color="none" onClick={() => setViewTicketModal(false)} />
              <ShowTicket inModal ticketId={selectedTicket?.id} handleDeleteTicket={handleDeleteTicket} />
            </MDBModalBody>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>

      {/* ── Order Modal ────────────────────────────────── */}
      <MDBModal open={viewOrderModal} onClose={() => setViewOrderModal(false)} position="top">
        <MDBModalDialog>
          <MDBModalContent>
            <MDBModalHeader>
              <MDBModalTitle>{t("Your order")}</MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={() => setViewOrderModal(false)} />
            </MDBModalHeader>
            <MDBModalBody>
              <h5>{t("Order details")}</h5>
              <p>
                {t("Order date")}: {isoToGermanDate(selectedOrder?.createdAt)}
              </p>
              {selectedOrder?.totalAmount != null && (
                <p>
                  {t("Total")}:{" "}
                  {selectedOrder.totalAmount.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                </p>
              )}

              {/* Line items */}
              {selectedOrder?.lineItems?.items?.length > 0 && (
                <>
                  <h6>{t("Products")}</h6>
                  {selectedOrder.lineItems.items.map((li, idx) => (
                    <div key={li.id || idx} className="d-flex justify-content-between small py-1 border-bottom">
                      <span className={li.isCanceled ? "text-decoration-line-through text-danger" : ""}>
                        {getLocalizedText(li.product?.name, i18n.language)}
                        {li.qty > 1 && ` × ${li.qty}`}
                      </span>
                      <span>{(li.price * (li.qty || 1) * (1 + (selectedOrder.financial?.taxRate || 0) / 100))?.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}</span>
                    </div>
                  ))}
                </>
              )}

              <h6>{t("Documents")}</h6>
              {(() => {
                const docs = selectedOrder?.financial?.documents
                  ?.filter(doc => doc.status !== "deleted")
                  ?.sort((a, b) => new Date(b.date) - new Date(a.date));

                if (docs?.length > 0) {
                  return docs.map((doc, idx) => (
                    <div key={doc.documentNumber || idx} className="d-flex align-items-center py-1 border-bottom small">
                      <a
                        href={`https://iec-cp-public.s3.amazonaws.com/${doc.s3Path}`}
                        target="_blank"
                        rel="noreferrer"
                        className={`d-flex align-items-center gap-2 ${doc.status === "cancelled" ? "text-decoration-line-through text-muted" : "text-primary"
                          }`}
                      >
                        <MDBIcon fas icon="file-invoice" />
                        <span>{t(doc.documentType)}</span>
                        <span>{doc.documentNumber}</span>
                        <span className="text-muted">
                          {new Date(doc.date).toLocaleDateString("de-DE")}
                        </span>
                      </a>
                    </div>
                  ));
                } else if (selectedOrder?.financial?.invoiceFile?.s3Path) {
                  return (
                    <a
                      href={`https://iec-cp-public.s3.amazonaws.com/${selectedOrder.financial.invoiceFile.s3Path}`}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-sm btn-outline-primary"
                    >
                      <MDBIcon fas icon="download" className="me-2" />
                      {t("Download invoice")}
                    </a>
                  );
                } else {
                  return <p className="text-muted small">{t("No invoice available yet")}</p>;
                }
              })()}
            </MDBModalBody>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>

      <StripeRedirectingOverlay show={isProcessingCheckout} />
    </>
  );
}